"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  fromGermanGrade,
  GERMAN_PRESETS,
  type GermanGradeResult,
  type GermanPreset,
  germanBand,
  toGermanGrade,
} from "@/lib/tool-kit/grades/german-grade";

/** English strings. A translated content file overrides these through `ui`. */
export const GERMAN_GRADE_CONVERTER_STRINGS = {
  presetLabel: "Your grading system",
  presets: {
    "us-gpa": "US GPA, 4.0 scale — best 4.0, pass 1.0",
    "percent-50": "Percentage — best 100, pass 50",
    "percent-40": "Percentage — best 100, pass 40",
    "uk-percent": "UK percentage marks — best 85, pass 40",
    "india-cgpa": "Indian 10-point CGPA — best 10, pass 4",
    "france-20": "French 0–20 scale — best 18, pass 10",
    "netherlands-10": "Dutch 1–10 scale — best 9, pass 6",
    custom: "Custom — type Nmax and Nmin",
  },
  presetHint:
    "Starting values only. Use the Nmax and Nmin your target university or uni-assist applies to your institution.",
  nmaxLabel: "Best possible grade (Nmax)",
  nminLabel: "Lowest passing grade (Nmin)",
  gradeLabel: "Your grade (Nd)",
  results: "Result",
  germanGrade: "German grade",
  exactValue: "Before truncation",
  band: "Rating",
  bands: {
    "sehr-gut": "sehr gut — very good",
    gut: "gut — good",
    befriedigend: "befriedigend — satisfactory",
    ausreichend: "ausreichend — sufficient",
    "nicht-ausreichend": "nicht ausreichend — fail",
  },
  working: "X = 1 + 3 × ({nmax} − {nd}) ÷ ({nmax} − {nmin}) = {exact}",
  truncated:
    "Kept to one decimal place without rounding, as the KMK rule requires: {grade}.",
  cappedNote:
    "Your grade is better than Nmax, so the result is capped at the best German grade, 1.0.",
  failingNote:
    "A result worse than 4.0 means your grade is below the passing mark you entered — it does not convert to a pass.",
  invalid:
    "Enter numbers for Nmax, Nmin and your grade, with Nmax different from Nmin.",
  reverseHeading: "German grade → your scale",
  reverseLabel: "German grade from 1.0 to 4.0",
  reverseResult: "A German {german} corresponds to {value} on your scale.",
  reverseInvalid: "Enter a German grade from 1.0 to 4.0.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "{nd} (Nmax {nmax}, Nmin {nmin}) = German grade {grade}, {band}",
} as const;

type Strings = typeof GERMAN_GRADE_CONVERTER_STRINGS;
type Format = ReturnType<typeof useToolNumber>;
type PresetChoice = GermanPreset | "custom";

function GradeResult({
  result,
  nd,
  nmax,
  nmin,
  strings,
  fmt,
}: {
  result: GermanGradeResult;
  nd: number;
  nmax: number;
  nmin: number;
  strings: Strings;
  fmt: Format;
}) {
  const grade = fmt(result.grade, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const exact = fmt(result.exact, { maximumFractionDigits: 4 });
  const plain = (value: number) => fmt(value, { maximumFractionDigits: 4 });
  const band = strings.bands[germanBand(result.grade)];
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.germanGrade} value={grade} />
        <StatCard label={strings.exactValue} value={exact} />
        <StatCard label={strings.band} value={band} />
      </StatGrid>
      <p className="rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm">
        {t(strings.working, {
          nmax: plain(nmax),
          nmin: plain(nmin),
          nd: plain(nd),
          exact,
        })}
      </p>
      <p className="text-muted-foreground text-sm">
        {t(strings.truncated, { grade })}
      </p>
      {result.capped ? <Notice>{strings.cappedNote}</Notice> : null}
      {result.failing ? <Notice>{strings.failingNote}</Notice> : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={t(strings.summary, {
          nd: plain(nd),
          nmax: plain(nmax),
          nmin: plain(nmin),
          grade,
          band,
        })}
      />
    </>
  );
}

function ReverseConverter({
  nmax,
  nmin,
  strings,
  fmt,
}: {
  nmax: number | null;
  nmin: number | null;
  strings: Strings;
  fmt: Format;
}) {
  const [germanRaw, setGermanRaw] = useState("2.0");
  const german = parseNumberInput(germanRaw);
  const valid = german !== null && german >= 1 && german <= 4;
  const ready = valid && nmax !== null && nmin !== null && nmax !== nmin;
  return (
    <section className={`${CARD} space-y-4`}>
      <h3 className={WIDGET_H3}>{strings.reverseHeading}</h3>
      <TextField
        id="ggc-german"
        label={strings.reverseLabel}
        numeric
        onChange={setGermanRaw}
        value={germanRaw}
      />
      <div aria-live="polite">
        {ready ? (
          <p className="text-foreground text-sm">
            {t(strings.reverseResult, {
              german: fmt(german, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 2,
              }),
              value: fmt(fromGermanGrade(german, nmax, nmin), {
                maximumFractionDigits: 3,
              }),
            })}
          </p>
        ) : (
          <Notice>{valid ? strings.invalid : strings.reverseInvalid}</Notice>
        )}
      </div>
    </section>
  );
}

export default function GermanGradeConverterWidget() {
  const strings = useToolUi(GERMAN_GRADE_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [preset, setPreset] = useState<PresetChoice>("us-gpa");
  const [nmaxRaw, setNmaxRaw] = useState("4");
  const [nminRaw, setNminRaw] = useState("1");
  const [gradeRaw, setGradeRaw] = useState("3.3");

  const nmax = parseNumberInput(nmaxRaw);
  const nmin = parseNumberInput(nminRaw);
  const nd = parseNumberInput(gradeRaw);
  const result =
    nmax === null || nmin === null || nd === null
      ? null
      : toGermanGrade(nd, nmax, nmin);

  function choosePreset(next: PresetChoice) {
    setPreset(next);
    if (next !== "custom") {
      setNmaxRaw(String(GERMAN_PRESETS[next].nmax));
      setNminRaw(String(GERMAN_PRESETS[next].nmin));
    }
  }

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-5`}>
        <SelectField
          hint={strings.presetHint}
          id="ggc-preset"
          label={strings.presetLabel}
          onChange={choosePreset}
          options={(Object.keys(strings.presets) as PresetChoice[]).map(
            (key) => ({ value: key, label: strings.presets[key] })
          )}
          value={preset}
        />
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            id="ggc-nmax"
            label={strings.nmaxLabel}
            numeric
            onChange={(value) => {
              setPreset("custom");
              setNmaxRaw(value);
            }}
            value={nmaxRaw}
          />
          <TextField
            id="ggc-nmin"
            label={strings.nminLabel}
            numeric
            onChange={(value) => {
              setPreset("custom");
              setNminRaw(value);
            }}
            value={nminRaw}
          />
          <TextField
            id="ggc-grade"
            label={strings.gradeLabel}
            numeric
            onChange={setGradeRaw}
            value={gradeRaw}
          />
        </div>
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {result === null || nmax === null || nmin === null || nd === null ? (
          <Notice>{strings.invalid}</Notice>
        ) : (
          <GradeResult
            fmt={fmt}
            nd={nd}
            nmax={nmax}
            nmin={nmin}
            result={result}
            strings={strings}
          />
        )}
      </section>

      <ReverseConverter fmt={fmt} nmax={nmax} nmin={nmin} strings={strings} />
    </div>
  );
}
